import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  Button,
  ButtonBar,
  ModalControlTriggerProps,
  NakedButton,
  Text,
  TextLink,
  useTooltip,
} from '@frontend/design-system';
import { ActiveTab, TrayMode } from '../types';

type FormModeCountProps = {
  recipientCount?: number;
};

const FormModeCount = ({ recipientCount }: FormModeCountProps) => {
  const { t } = useTranslation('bulk-messaging');

  return (
    <Text css={{ textAlign: 'right' }}>
      {recipientCount === undefined
        ? t('Counting Contacts...')
        : t('{{count}} Contacts Match Your Filters', { count: recipientCount })}
    </Text>
  );
};

type DisplayModeCountProps = {
  recipientCount?: number;
  duplicateCount?: number;
  sourceText: string;
};

const DisplayModeCount = ({ recipientCount, duplicateCount, sourceText }: DisplayModeCountProps) => {
  const { t } = useTranslation('bulk-messaging');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top-end' });

  return (
    <>
      <div css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Text color='subdued' css={{ fontSize: theme.fontSize(24) }} weight='bold'>
          {t('{{count}} Contact', { count: recipientCount })}
        </Text>
        <Text css={{ display: 'flex', alignItems: 'center' }}>
          {sourceText}
          <NakedButton {...triggerProps}>
            <Icon name='info-small' css={{ marginLeft: theme.spacing(0.5) }} />
          </NakedButton>
        </Text>
        <Tooltip {...tooltipProps}>
          {t(
            'If identical contacts are found across segments, they will be de-duplicated upon the creation of this audience. For this reason, your final audience could be slightly smaller than predicted.'
          )}
        </Tooltip>
      </div>
      {!!duplicateCount && (
        <BannerNotification
          message={t(
            '{{count}} contacts will receive this campaign more than once, due to being associated with multiple selected locations.',
            { count: duplicateCount }
          )}
          status='info'
          css={{ marginTop: theme.spacing(2) }}
        />
      )}
    </>
  );
};

type TrayFooterProps = FormModeCountProps &
  DisplayModeCountProps & {
    isValid: boolean;
    mode: TrayMode;
    onCancel: () => void;
    onConfirm: () => void;
    tabMode: ActiveTab;
    trackingIdPrefix: string;
    triggerProps: ModalControlTriggerProps;
  };

export const TrayFooter = ({
  recipientCount,
  duplicateCount,
  isValid,
  mode,
  onCancel,
  onConfirm,
  sourceText,
  tabMode,
  trackingIdPrefix,
  triggerProps,
}: TrayFooterProps) => {
  const { t } = useTranslation('bulk-messaging');

  return (
    <>
      <footer css={{ borderTop: `1px solid ${theme.colors.neutral20}`, padding: theme.spacing(2, 4, 0) }}>
        {mode === 'form' ? (
          <FormModeCount recipientCount={recipientCount} />
        ) : (
          <DisplayModeCount recipientCount={recipientCount} duplicateCount={duplicateCount} sourceText={sourceText} />
        )}
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: theme.spacing(3),
          }}
        >
          <TextLink
            css={{
              display: 'flex',
              alignItems: 'center',
              columnGap: theme.spacing(0.75),
              minWidth: 'fit-content',
            }}
            trackingId={`${trackingIdPrefix}-message-allotment-btn`}
            {...triggerProps}
          >
            <Icon name='locations' />
            <Text as='span' weight='bold' color='primary'>
              {t('Message Allotment')}
            </Text>
          </TextLink>
          <ButtonBar css={{ justifyContent: 'flex-end', padding: 0 }}>
            {mode === 'form' && (
              <Button variant='secondary' onClick={onCancel} trackingId={`${trackingIdPrefix}-filter-back-btn`}>
                {t('Back')}
              </Button>
            )}
            <Button onClick={onConfirm} trackingId={`${trackingIdPrefix}-filter-apply-btn`} disabled={!isValid}>
              {mode === 'form' ? t('Create Segment') : tabMode === 'saved-lists' ? t('Apply Lists') : t('Close')}
            </Button>
          </ButtonBar>
        </div>
      </footer>
    </>
  );
};
